import { DashboardState, IAction, IDisplayItem, IEvent, IScreenDesign, SpecialDIIds } from "@kuika/kuika-cl-model";
import _ from "lodash";
import { IScreenDesignContextState } from "../../contexts/screen-design-context/screen-design-context";
import { IActionClipboard } from "../../models/interface/action-clipboard";
import ActionService from "../../services/action-service";
import {
  clearPastedLoopItemParameters,
  collectCurrentRowSources,
  rebindPastedCurrentRowSymbols
} from "./current-row-source";
import { findDesignerActionInTree } from "./designer-action-context-actions";
import type { ScreenDesignInstance } from "./designer-context";
import { resolveEditTarget } from "./detached-screen-session";
import { KMainFunctions } from "./main-functions";
import { ScreenDesignHelper } from "./screen-design-helper";

function getNavigationDisplayItem(sd: IScreenDesignContextState, action: IAction): IDisplayItem | undefined {
  if (action.label !== "NAVIGATE" && action.label !== "NavigateToTask") {
    return undefined;
  }

  const navKuikaIdParameter = action.parameters.find((parameter) => parameter.name === "navigationKuikaId");
  const navKuikaId = navKuikaIdParameter?.symbol?.fixedValue;
  if (!navKuikaId) {
    return undefined;
  }

  return sd.screenDesignDictionary?.[parseInt(navKuikaId, 10)];
}

/** The component whose event holds `actionId`, at any depth inside If / ForEach / Recurring / MQTT actions. */
function findActionHostKuikaId(sd: IScreenDesignContextState, actionId: string): number | undefined {
  const host: IDisplayItem | undefined = Object.values(sd.screenDesignDictionary ?? {}).find(
    (di: IDisplayItem): boolean =>
      (di.item?.events ?? []).some(
        (event: IEvent): boolean => findDesignerActionInTree(event.actions ?? [], actionId) !== null
      )
  );
  return host === undefined ? undefined : Number(host.id);
}

/**
 * The component a "+ PASTE" or If / Else paste lands on: the selection — but only where actions live
 * on components. Scheduled jobs, API methods, workflow steps and the rest keep their actions
 * elsewhere, and a canvas selection left over from the screen editor is not their host.
 */
export function getDesignerActionPasteHostKuikaId(sd: IScreenDesignContextState): number | undefined {
  if (sd.dashboardState !== DashboardState.design && sd.dashboardState !== DashboardState.reportDesigner) {
    return undefined;
  }
  const kuikaId: number = Number(sd.selectedKuikaIds?.[0]);
  return Number.isFinite(kuikaId) && sd.screenDesignDictionary?.[kuikaId] ? kuikaId : undefined;
}

/** "+ PASTE" and If / Else paste: re-anchor or clear the pasted action's Current Row values (KOA-16658). */
export function rebindDesignerPastedAction(
  action: IAction,
  sd: IScreenDesignContextState,
  clipboard: IActionClipboard
): void {
  rebindPastedCurrentRowSymbols(action, sd, getDesignerActionPasteHostKuikaId(sd), clipboard.currentRowSources);
  clearPastedLoopItemParameters(action);
}

export function buildDesignerActionClipboard(
  sdInstance: ScreenDesignInstance,
  action: IAction,
  eventName: string,
  screenId?: string | null
): IActionClipboard {
  // Copy records WHICH screen the action came from, so a paste can tell same-screen from
  // cross-screen. Omitted → the open screen, as before.
  const sd = resolveEditTarget(sdInstance, screenId);
  return {
    action: _.cloneDeep(action),
    eventName,
    navigationDI: _.cloneDeep(getNavigationDisplayItem(sd, action)),
    currentRowSources: collectCurrentRowSources(action, sd)
  };
}

export function pasteDesignerActionClipboard(
  sdInstance: ScreenDesignInstance,
  targetAction: IAction,
  clipboard: IActionClipboard,
  screenId?: string | null
): {
  action: IAction;
  screenDesign: IScreenDesign;
  screenDesignChanged: boolean;
} {
  // The paste lands on the screen the editor is hosted over: the new navigation component's id is
  // generated against THAT screen's dictionary, and the tree it returns is that screen's tree.
  const sd = resolveEditTarget(sdInstance, screenId);
  const action = _.cloneDeep(clipboard.action);
  const navigationDisplayItem = _.cloneDeep(clipboard.navigationDI);
  let newNavigationDisplayItemId: number | undefined;

  if (navigationDisplayItem) {
    newNavigationDisplayItemId = KMainFunctions.generateDisplayItemId(sd.screenDesignDictionary);
    const navigationKuikaIdParameter = action.parameters.find((parameter) => parameter.name === "navigationKuikaId");
    if (navigationKuikaIdParameter?.symbol) {
      navigationKuikaIdParameter.symbol.fixedValue = newNavigationDisplayItemId;
    }
  }

  ActionService.setNewIdsForAction(action);
  action.id = targetAction.id;
  rebindPastedCurrentRowSymbols(action, sd, findActionHostKuikaId(sd, targetAction.id), clipboard.currentRowSources);

  const screenDesign = sd.getScreenDesign();
  let screenDesignChanged = false;
  let navigationDisplayItemIdToRemove: number | undefined;

  if (targetAction.label === "NAVIGATE" || targetAction.label === "NavigateToTask") {
    const oldNavigationParameter = targetAction.parameters.find((parameter) => parameter.name === "navigationKuikaId");
    if (oldNavigationParameter?.symbol?.fixedValue) {
      navigationDisplayItemIdToRemove = oldNavigationParameter.symbol.fixedValue;
    }
  }

  if (navigationDisplayItem && newNavigationDisplayItemId) {
    const renewedDisplayItem = ScreenDesignHelper.renewDisplayItem(navigationDisplayItem, sd.screenDesignDictionary);
    renewedDisplayItem.id = newNavigationDisplayItemId;
    renewedDisplayItem.parentId = SpecialDIIds.rootDI;
    renewedDisplayItem.children.forEach((child) => {
      child.parentId = renewedDisplayItem.id;
    });

    const rootDisplayItemIndex = ScreenDesignHelper.getRootDiIdxInDisplayItems(screenDesign.displayItems);
    if (rootDisplayItemIndex >= 0) {
      screenDesign.displayItems[rootDisplayItemIndex].children.push(renewedDisplayItem);
      screenDesignChanged = true;
    }
  }

  if (navigationDisplayItemIdToRemove) {
    const rootDisplayItem = screenDesign.displayItems.find((displayItem) => displayItem.id === SpecialDIIds.rootDI);
    if (rootDisplayItem?.children) {
      rootDisplayItem.children = rootDisplayItem.children.filter(
        (displayItem) => displayItem.id !== navigationDisplayItemIdToRemove
      );
      screenDesignChanged = true;
    }
  }

  return {
    action,
    screenDesign,
    screenDesignChanged
  };
}
